import React, { FC, useState } from "react";
import { object, string } from "yup";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import DialogLayout from "../DialogLayout";
import Typography from "../../components/Typography/Typography";
import ASSETS from "../../assets";
import TextField from "../../components/TextField/TextField";
import Button from "../../components/Button/Button";
import { authAPI, AddAzureUserRequest } from "../../services/api";

interface IProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSuccess: () => void;
}

const schema = object({
  email: string()
    .required("Email is required")
    .email("Please enter a valid email address"),
  firstName: string()
    .required("First name is required")
    .max(20, "First name cannot exceed 20 characters")
    .matches(
      /^[A-Za-z\s]*$/,
      "First name cannot contain numbers or special characters"
    ),
  lastName: string()
    .required("Last name is required")
    .max(20, "Last name cannot exceed 20 characters")
    .matches(
      /^[A-Za-z\s]*$/,
      "Last name cannot contain numbers or special characters"
    ),
});

const AddAzureUser: FC<IProps> = ({ open, setOpen, onSuccess }) => {
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: "",
      firstName: "",
      lastName: "",
    },
    validationSchema: schema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        
        const addData: AddAzureUserRequest = {
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
        };

        const response = await authAPI.addAzureUser(addData);
        
        // Show success message
        toast.success(response.message || 'Azure user added successfully!');
        
        // Close modal immediately
        handleClose();
        
        // Refresh Azure users data - first call immediately
        onSuccess();
        
        // Second API call after 2 seconds
        setTimeout(() => {
          onSuccess();
        }, 2000);
        
      } catch (err: any) {
        let errorMessage = 'Failed to add Azure user. Please try again.';
        
        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response?.status === 400) {
          errorMessage = 'Invalid user data. Please check and try again.';
        } else if (err.response?.status === 409) {
          errorMessage = 'This email is already in use.';
        } else if (err.response?.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
  });

  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
  };

  return (
    <DialogLayout open={open}>
      <div className="w-full max-w-[536px] p-[25px] bg-white rounded-[15px] relative">
        <div
          className="absolute top-[25px] right-[25px] rounded-full size-[30px] bg-[#0000000D] cursor-pointer grid place-items-center"
          onClick={handleClose}
        >
          <img src={ASSETS.cross} alt="" />
        </div>
        <Typography variant="h2" className="font-bold title">
          Add Azure User
        </Typography>
        <Typography
          variant="body1"
          color="grey"
          className="mt-[5px] font-medium"
        >
          Enter the details of the Azure user you want to add.
        </Typography>
        <div className="border border-k-border rounded-lg p-6 mt-[25px] flex flex-col gap-6">
          <TextField
            label="Email Address"
            name="email"
            type="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.email && formik.errors.email
                ? formik.errors.email
                : ""
            }
            onClear={() => formik.setFieldValue("email", "")}
            required
          />
          <TextField
            label="First Name"
            name="firstName"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.firstName && formik.errors.firstName
                ? formik.errors.firstName
                : ""
            }
            onClear={() => formik.setFieldValue("firstName", "")}
            required
          />
          <TextField
            label="Last Name"
            name="lastName"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.lastName && formik.errors.lastName
                ? formik.errors.lastName
                : ""
            }
            onClear={() => formik.setFieldValue("lastName", "")}
            required
          />
          <Button
            label={loading ? "Adding..." : "Add User"}
            variant="primary"
            className="w-full h-[45px] disabled:opacity-200 disabled:cursor-not-allowed disabled:!text-gray-400"
            onClick={() => formik.handleSubmit()}
            loading={loading}
            disabled={loading}
            type="button"
          />
        </div>
      </div>
    </DialogLayout>
  );
};

export default AddAzureUser;
